import React, { useState, useEffect, useCallback } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedToggle } from "@/components/ThemedToggle";
import { ThemedSegmentedControl } from "@/components/ThemedSegmentedControl";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedList, type ISortOption, type IGroupOption, type ThemedListViewMode } from "@/components/ThemedList";
import { ThemedView } from "@/components/ThemedView";
import { ComponentRefTag } from "@/components/design/ComponentRefTag";
import { ThemedChip, ThemedCompactCard, ThemedMediumCard, ThemedFullCard, ThemedCustomCard, MessageCard } from "@/components/cards";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useColorScheme } from "@/hooks/useColorScheme";
import { Spacing, Colors } from "@/constants/ThemeColours";
import { Ionicons } from "@expo/vector-icons";

import {
  MemoryModel,
  ContactModel,
  GroupModel,
  MediaModel,
  ShareModel,
  TagModel,
  formatLocation,
  formatDisplayDate,
  ICustomCardEntityConfig,
} from "common247";
import { MemoryCard } from "@/components/memory/MemoryCard";
import { ContactCard } from "@/components/contacts/ContactCard";
import { GroupCard } from "@/components/groups/GroupCard";
import { MediaCard } from "@/components/media/MediaCard";
import { MEDIA_FAVOURITE_FILTER, MEDIA_FAVOURITE_GROUP_OPTION, formatFavouriteRatingGroup } from "@/components/media/MediaGallery";
import { SharingCard } from "@/components/sharing/SharingCard";
import { ThemedViewSelector } from "@/components/ThemedViewSelector";
import { entityCache } from "@/services/sync/entity-cache";

type EntityType = "memories" | "contacts" | "groups" | "media" | "tags" | "shares";
export type DesignViewMode = ThemedListViewMode;

type CustomConfigEntity = "memory" | "media" | "contact" | "group" | "tag" | "share" | "message";

const ENTITY_CONFIG_OPTIONS: Record<
  CustomConfigEntity,
  {
    fields: { key: string; label: string }[];
    sections: { key: string; label: string }[];
  }
> = {
  memory: {
    fields: [
      { key: "title", label: "Title" },
      { key: "date", label: "Date" },
      { key: "description", label: "Description" },
      { key: "ownerDisplayName", label: "Owner" },
      { key: "favouriteRating", label: "Rating Stars" },
    ],
    sections: [
      { key: "media", label: "Media Tray" },
      { key: "links", label: "Links" },
      { key: "tags", label: "Tags" },
      { key: "shares", label: "Sharing" },
    ],
  },
  media: {
    fields: [
      { key: "title", label: "Title" },
      { key: "mediaDate", label: "Captured Date" },
      { key: "description", label: "Description" },
      { key: "thumbnail", label: "Thumbnail Photo" },
      { key: "favouriteRating", label: "Rating Stars" },
    ],
    sections: [
      { key: "links", label: "Links" },
      { key: "tags", label: "Tags" },
      { key: "shares", label: "Sharing" },
    ],
  },
  contact: {
    fields: [
      { key: "displayName", label: "Display Name" },
      { key: "email", label: "Email Address" },
      { key: "photo", label: "Profile Photo" },
      { key: "ownerDisplayName", label: "Added By" },
    ],
    sections: [
      { key: "links", label: "Links" },
      { key: "tags", label: "Tags" },
      { key: "shares", label: "Sharing" },
    ],
  },
  group: {
    fields: [
      { key: "name", label: "Group Name" },
      { key: "description", label: "Description" },
      { key: "photo", label: "Group Icon" },
      { key: "ownerDisplayName", label: "Admin Owner" },
    ],
    sections: [
      { key: "links", label: "Links" },
      { key: "shares", label: "Sharing" },
    ],
  },
  tag: {
    fields: [
      { key: "name", label: "Tag Name" },
      { key: "tagType", label: "Category / Type" },
      { key: "description", label: "Description" },
    ],
    sections: [
      { key: "links", label: "Links" },
      { key: "media", label: "Linked Media" },
    ],
  },
  share: {
    fields: [
      { key: "sharedItem", label: "Shared Resource" },
      { key: "recipientDisplayName", label: "Recipient Name" },
      { key: "role", label: "Access Permission" },
      { key: "date", label: "Share Date" },
    ],
    sections: [
      { key: "sharedItem", label: "Shared Item Preview" },
      { key: "shares", label: "Collaborators" },
    ],
  },
  message: {
    fields: [
      { key: "subject", label: "Subject" },
      { key: "sender", label: "Sender Name" },
      { key: "recipient", label: "Recipient Name" },
      { key: "date", label: "Timestamp" },
      { key: "messageType", label: "Message Type" },
      { key: "unreadStatus", label: "Read Status" },
    ],
    sections: [],
  },
};

const VIEW_MODES: { key: DesignViewMode; icon: keyof typeof Ionicons.glyphMap; title: string; label: string }[] = [
  { key: "chip", icon: "pricetag-outline", title: "Chip (Tier 1)", label: "Chip" },
  { key: "compact", icon: "reorder-four-outline", title: "Compact (Tier 2)", label: "Compact" },
  { key: "medium", icon: "apps-outline", title: "Medium (Tier 3)", label: "Medium" },
  { key: "full", icon: "newspaper-outline", title: "Full (Tier 4)", label: "Full" },
  { key: "custom", icon: "options-outline", title: "Custom (Tier 5)", label: "Custom" },
  { key: "details", icon: "list-outline", title: "Details (Rows)", label: "Details" },
];

/**
 * Mock data for the 5 platform entity types.
 */
const MOCK_MEMORIES: MemoryModel[] = [
  {
    id: "mem-1",
    ownerId: "u-1",
    ownerDisplayName: "Mara Solis",
    title: "Kyoto Bamboo Grove Walk",
    description: "Serene morning walk through Arashiyama bamboo forest with traditional matcha tasting.",
    date: "2026-08-15",
    location: { text: "Kyoto, Japan" },
    mediaIds: ["med-1"],
    media: [
      {
        id: "med-1",
        ownerId: "u-1",
        downloadUrl: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=500",
        favouriteRating: 5,
        type: "image",
        createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
      } as any,
    ],
    userRole: "owner",
    canShare: true,
    sharedWith: [
      { id: "cnt-1", displayName: "Alice Walker", type: "contact", role: "editor" },
      { id: "cnt-2", displayName: "Charlie Brown", type: "contact", role: "viewer" },
    ],
    createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
  },
  {
    id: "mem-2",
    ownerId: "u-2",
    ownerDisplayName: "Sarah Connor",
    title: "Swiss Alpine Hiking Trail",
    description: "Panoramic vistas over Matterhorn with fresh alpine breeze and trail lunch.",
    date: "2026-07-22",
    location: { text: "Zermatt, Switzerland" },
    mediaIds: ["med-2"],
    media: [
      {
        id: "med-2",
        ownerId: "u-2",
        downloadUrl: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500",
        favouriteRating: 4,
        type: "image",
        createdAt: Date.parse("2026-07-22T14:30:00.000Z"),
      } as any,
    ],
    userRole: "viewer",
    canShare: false,
    createdAt: Date.parse("2026-07-22T14:30:00.000Z"),
  },
  {
    id: "mem-3",
    ownerId: "u-1",
    ownerDisplayName: "Mara Solis",
    title: "Santorini Sunset Sailing",
    description: "Catamaran cruise around the volcanic caldera with sunset seafood dinner.",
    date: "2026-09-02",
    location: { text: "Oia, Santorini" },
    mediaIds: ["med-3"],
    media: [
      {
        id: "med-3",
        ownerId: "u-1",
        downloadUrl: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=500",
        favouriteRating: 5,
        type: "image",
        createdAt: Date.parse("2026-09-02T19:00:00.000Z"),
      } as any,
    ],
    userRole: "owner",
    canShare: true,
    createdAt: Date.parse("2026-09-02T19:00:00.000Z"),
  },
];

const MOCK_CONTACTS: ContactModel[] = [
  {
    id: "cnt-1",
    displayName: "Alice Walker",
    email: "alice.walker@example.com",
    userRole: "owner",
    ownerId: "u-1",
    createdAt: Date.parse("2026-01-10T10:00:00.000Z"),
    isActive: true,
    canShare: true,
    memoryItems: [
      {
        id: "mem-1",
        title: "Kyoto Bamboo Grove Walk",
        ownerId: "u-1",
        createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
      } as any,
    ],
    sharedWith: [
      { id: "cnt-2", displayName: "Charlie Brown", type: "contact", role: "viewer" },
      { id: "grp-1", displayName: "Family Heritage", type: "group", role: "editor" },
    ],
  },
  {
    id: "cnt-2",
    displayName: "Charlie Brown",
    email: "charlie.brown@example.com",
    userRole: "viewer",
    ownerId: "u-2",
    ownerDisplayName: "Robert Green",
    createdAt: Date.parse("2026-02-14T15:30:00.000Z"),
    isActive: true,
    memoryItems: [
      {
        id: "mem-2",
        title: "Swiss Alpine Hiking Trail",
        ownerId: "u-2",
        createdAt: Date.parse("2026-07-22T14:30:00.000Z"),
      } as any,
    ],
  },
  {
    id: "cnt-3",
    displayName: "Diana Prince",
    email: "diana.prince@example.com",
    userRole: "owner",
    ownerId: "u-1",
    createdAt: Date.parse("2026-03-01T09:15:00.000Z"),
    isActive: true,
  },
];

const MOCK_GROUPS: GroupModel[] = [
  {
    id: "grp-1",
    name: "Family Heritage",
    description: "Shared memories, vintage photos, and family lineage.",
    userRole: "owner",
    ownerId: "u-1",
    canShare: true,
    contactIds: ["cnt-1", "cnt-2"],
    memoryItems: [
      {
        id: "mem-1",
        title: "Kyoto Bamboo Grove Walk",
        ownerId: "u-1",
        createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
      } as any,
    ],
    sharedWith: [
      { id: "cnt-1", displayName: "Alice Walker", type: "contact", role: "editor" },
      { id: "cnt-2", displayName: "Charlie Brown", type: "contact", role: "viewer" },
    ],
    createdAt: Date.parse("2026-01-01T00:00:00.000Z"),
    updatedAt: Date.parse("2026-06-01T00:00:00.000Z"),
  },
  {
    id: "grp-2",
    name: "Travel Companions",
    description: "Global trips, itineraries, hotel recommendations, and group memories.",
    userRole: "owner",
    ownerId: "u-1",
    contactIds: ["cnt-1", "cnt-3"],
    memoryItems: [
      {
        id: "mem-3",
        title: "Santorini Sunset Sailing",
        ownerId: "u-1",
        createdAt: Date.parse("2026-09-02T19:00:00.000Z"),
      } as any,
    ],
    createdAt: Date.parse("2026-03-15T00:00:00.000Z"),
    updatedAt: Date.parse("2026-08-20T00:00:00.000Z"),
  },
];

const MOCK_MEDIA: MediaModel[] = [
  {
    id: "med-1",
    ownerId: "u-1",
    title: "Kyoto_Bamboo_Path.jpg",
    filename: "Kyoto_Bamboo_Path.jpg",
    contentType: "image/jpeg",
    mediaType: "image",
    url: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=500",
    downloadUrl: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=500",
    favouriteRating: 5,
    userRole: "owner",
    canShare: true,
    createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
    mediaDate: {
      type: "date",
      formatted: "15-Aug-2026",
      from: "2026-08-15",
      to: "2026-08-15",
      userDefined: true,
    },
  } as any,
  {
    id: "med-2",
    ownerId: "u-2",
    title: "Alpine_Summit_Matterhorn.jpg",
    filename: "Alpine_Summit_Matterhorn.jpg",
    contentType: "image/jpeg",
    mediaType: "image",
    url: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500",
    downloadUrl: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500",
    favouriteRating: 4,
    userRole: "viewer",
    canShare: false,
    createdAt: Date.parse("2026-07-22T14:30:00.000Z"),
    mediaDate: {
      type: "date",
      formatted: "22-Jul-2026",
      from: "2026-07-22",
      to: "2026-07-22",
      userDefined: false,
    },
  } as any,
];

const MOCK_SHARES: ShareModel[] = [
  {
    id: "shr-1",
    ownerId: "u-1",
    targetType: "memory",
    targetId: "mem-1",
    targetDisplayName: "Alice Walker",
    recipientDisplayName: "Alice Walker",
    role: "editor",
    createdAt: Date.parse("2026-08-16T10:00:00.000Z"),
    sourceType: "memory",
    sharedItem: {
      entityType: "memory",
      id: "mem-1",
      title: "Kyoto Bamboo Grove Walk",
      date: "2026-08-15",
    },
  } as any,
  {
    id: "shr-2",
    ownerId: "u-1",
    targetType: "group",
    targetId: "grp-1",
    targetDisplayName: "Charlie Brown",
    recipientDisplayName: "Charlie Brown",
    role: "viewer",
    createdAt: Date.parse("2026-08-17T11:00:00.000Z"),
    sourceType: "group",
    sharedItem: {
      entityType: "group",
      id: "grp-1",
      title: "Family Heritage",
      date: "2026-01-01",
    },
  } as any,
];

const MOCK_TAGS: TagModel[] = [
  {
    id: "tag-1",
    value: "Kyoto Expedition",
    tagType: "memory",
    targetId: "mem-1",
    targetTitle: "Kyoto Bamboo Grove Walk",
    ownerId: "u-1",
    ownerDisplayName: "Mara Solis",
    createdAt: Date.parse("2026-08-15T10:00:00.000Z"),
    memoriesList: [
      { id: "mem-1", title: "Kyoto Bamboo Grove Walk" },
    ],
    mediaList: [
      { id: "med-1", title: "Bamboo Canopy", downloadUrl: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=500" },
    ],
    contactsList: [
      { id: "cnt-1", displayName: "Alice Walker" },
    ],
    groupsList: [
      { id: "grp-1", name: "Family Heritage" },
    ],
  } as any,
  {
    id: "tag-2",
    value: "Alpine Trail",
    tagType: "media",
    targetId: "med-2",
    targetTitle: "Matterhorn Peak",
    targetThumbnailUrl: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500",
    ownerId: "u-2",
    ownerDisplayName: "Sarah Connor",
    createdAt: Date.parse("2026-07-22T14:30:00.000Z"),
    mediaList: [
      { id: "med-2", title: "Matterhorn Peak", downloadUrl: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500" },
    ],
    memoriesList: [
      { id: "mem-2", title: "Swiss Alpine Hiking Trail" },
    ],
  } as any,
  {
    id: "tag-3",
    value: "Sunset Sailing",
    tagType: "memory",
    targetId: "mem-3",
    targetTitle: "Santorini Sunset Sailing",
    ownerId: "u-1",
    ownerDisplayName: "Mara Solis",
    createdAt: Date.parse("2026-09-02T19:00:00.000Z"),
    memoriesList: [
      { id: "mem-3", title: "Santorini Sunset Sailing" },
    ],
    mediaList: [
      { id: "med-3", title: "Oia Sunset", downloadUrl: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=500" },
    ],
  } as any,
];

const DEMO_TAG = {
  id: "demo-tag-1",
  name: "Wildflower Ridge",
  tagType: "memory",
};

const DEMO_SHARE = {
  id: "demo-share-1",
  targetId: "mem-1",
  targetType: "memory" as const,
  recipientUserId: "u-2",
  recipientDisplayName: "Arthur Pendelton",
  sharedItem: {
    entityType: "memory" as const,
    id: "mem-1",
    title: "Kyoto Bamboo Grove Walk",
    date: "2026-08-15",
  },
};

const DEMO_MESSAGE = {
  id: "msg-demo-1",
  subject: "Kyoto Bamboo Grove Photos Ready",
  body: "Hello! I have uploaded the photos from the Kyoto bamboo path expedition. Let me know what you think!",
  type: "User",
  senderUserId: "u-1",
  senderDisplayName: "Mara Solis",
  senderEmail: "mara.solis@example.com",
  recipients: [
    {
      contactId: "cnt-1",
      displayName: "Alice Walker",
      email: "alice.walker@example.com",
      status: "sent",
      read: false,
    },
  ],
  createdAt: Date.parse("2026-08-16T14:30:00.000Z"),
  updatedAt: Date.parse("2026-08-16T14:30:00.000Z"),
};

/**
 * Themed List & Card Workbench Screen.
 * Demonstrates the 5-Tier Item Card architecture (Chip, Compact, Medium, Full, Custom)
 * and the generic ThemedList component container across Memories, Contacts, Groups, Media, and Shares.
 */
export default function ThemedListDesignScreen() {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const colors = Colors[isDark ? "dark" : "light"];
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");

  const {
    tab: paramTab,
    entity: paramEntity,
    viewMode: paramViewMode,
    configEntity: paramConfigEntity,
    mediumEntity: paramMediumEntity,
    fullEntity: paramFullEntity,
  } = useLocalSearchParams<{
    tab?: string;
    entity?: string;
    viewMode?: string;
    configEntity?: string;
    mediumEntity?: string;
    fullEntity?: string;
  }>();

  // Normalize entity param (support singular or plural e.g. "memory" or "memories")
  const resolveEntityParam = (val?: string): EntityType | undefined => {
    if (!val) return undefined;
    const lower = val.toLowerCase();
    if (lower === "memory" || lower === "memories") return "memories";
    if (lower === "contact" || lower === "contacts") return "contacts";
    if (lower === "group" || lower === "groups") return "groups";
    if (lower === "media") return "media";
    if (lower === "tag" || lower === "tags") return "tags";
    if (lower === "share" || lower === "shares") return "shares";
    return undefined;
  };

  const resolveViewModeParam = (val?: string): DesignViewMode | undefined => {
    if (!val) return undefined;
    const valid: DesignViewMode[] = ["chip", "compact", "medium", "full", "custom", "details"];
    return valid.includes(val as DesignViewMode) ? (val as DesignViewMode) : undefined;
  };

  const resolveWorkbenchTab = (val?: string): "cards" | "lists" => {
    return val === "lists" || val === "list" ? "lists" : "cards";
  };

  // Top-Level Workbench Switcher: Cards vs Lists (initializes from ?tab=)
  const [workbenchTab, setWorkbenchTab] = useState<"cards" | "lists">(() => resolveWorkbenchTab(paramTab));

  useEffect(() => {
    entityCache.setMany("contact", MOCK_CONTACTS);
    entityCache.setMany("group", MOCK_GROUPS);
    entityCache.setMany("media", MOCK_MEDIA);
    entityCache.setMany("memory", MOCK_MEMORIES);
  }, []);

  // Card Workbench States
  const [cardSelectionMode, setCardSelectionMode] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [selectedMediumEntity, setSelectedMediumEntity] = useState<CardEntityType>(
    () => (paramMediumEntity as CardEntityType) || "memory"
  );
  const [selectedFullEntity, setSelectedFullEntity] = useState<CardEntityType>(
    () => (paramFullEntity as CardEntityType) || "memory"
  );
  const [selectedConfigEntity, setSelectedConfigEntity] = useState<CustomConfigEntity>(
    () => (paramConfigEntity as CustomConfigEntity) || "memory"
  );
  const [userCustomCardConfig, setUserCustomCardConfig] = useState<Record<CustomConfigEntity, ICustomCardEntityConfig>>({
    memory: {
      fields: ["title", "date", "description", "ownerDisplayName"],
      sections: ["media", "links", "tags", "shares"],
    },
    media: {
      fields: ["title", "mediaDate", "description", "favouriteRating"],
      sections: ["links", "tags", "shares"],
    },
    contact: {
      fields: ["displayName", "email", "photo"],
      sections: ["links", "tags"],
    },
    group: {
      fields: ["name", "description", "photo"],
      sections: ["links", "shares"],
    },
    tag: {
      fields: ["name", "tagType", "description"],
      sections: ["links", "media"],
    },
    share: {
      fields: ["sharedItem", "recipientDisplayName", "role", "date"],
      sections: ["sharedItem", "shares"],
    },
    message: {
      fields: ["subject", "sender", "recipient", "date"],
      sections: [],
    },
  });

  // List Workbench States (initializes from ?entity= and ?viewMode=)
  const [activeTab, setActiveTab] = useState<EntityType>(
    () => resolveEntityParam(paramEntity) || "memories"
  );
  const [viewMode, setViewMode] = useState<DesignViewMode>(
    () => resolveViewModeParam(paramViewMode) || "medium"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [enableHover, setEnableHover] = useState(true);

  // Sync state when URL params change (e.g. browser back/forward or external navigation)
  useEffect(() => {
    if (paramTab) {
      const resolved = resolveWorkbenchTab(paramTab);
      if (resolved !== workbenchTab) setWorkbenchTab(resolved);
    }
  }, [paramTab]);

  useEffect(() => {
    if (paramEntity) {
      const resolved = resolveEntityParam(paramEntity);
      if (resolved && resolved !== activeTab) setActiveTab(resolved);
    }
  }, [paramEntity]);

  useEffect(() => {
    if (paramViewMode) {
      const resolved = resolveViewModeParam(paramViewMode);
      if (resolved && resolved !== viewMode) setViewMode(resolved);
    }
  }, [paramViewMode]);

  useEffect(() => {
    if (paramConfigEntity && paramConfigEntity !== selectedConfigEntity) {
      setSelectedConfigEntity(paramConfigEntity as CustomConfigEntity);
    }
  }, [paramConfigEntity]);

  useEffect(() => {
    if (paramMediumEntity && paramMediumEntity !== selectedMediumEntity) {
      setSelectedMediumEntity(paramMediumEntity as CardEntityType);
    }
  }, [paramMediumEntity]);

  useEffect(() => {
    if (paramFullEntity && paramFullEntity !== selectedFullEntity) {
      setSelectedFullEntity(paramFullEntity as CardEntityType);
    }
  }, [paramFullEntity]);

  // Handlers that synchronize local state with URL search parameters
  const handleWorkbenchTabChange = useCallback((nextTab: "cards" | "lists") => {
    setWorkbenchTab(nextTab);
    router.setParams({ tab: nextTab });
  }, []);

  const handleActiveTabChange = useCallback((nextEntity: EntityType) => {
    setActiveTab(nextEntity);
    router.setParams({ entity: nextEntity });
  }, []);

  const handleViewModeChange = useCallback((nextMode: DesignViewMode) => {
    setViewMode(nextMode);
    router.setParams({ viewMode: nextMode });
  }, []);

  const handleConfigEntityChange = useCallback((nextEntity: CustomConfigEntity) => {
    setSelectedConfigEntity(nextEntity);
    router.setParams({ configEntity: nextEntity });
  }, []);

  const handleMediumEntityChange = useCallback((nextEntity: CardEntityType) => {
    setSelectedMediumEntity(nextEntity);
    router.setParams({ mediumEntity: nextEntity });
  }, []);

  const handleFullEntityChange = useCallback((nextEntity: CardEntityType) => {
    setSelectedFullEntity(nextEntity);
    router.setParams({ fullEntity: nextEntity });
  }, []);

  // Sort Options per Entity
  const memorySortOptions: ISortOption<MemoryModel>[] = [
    { key: "date", label: "Date (Chronological)", getValue: (m) => new Date(m.date || m.createdAt || 0) },
    { key: "favouriteRating", label: "Favourite Rating", getValue: (m) => m.favouriteRating ?? 3 },
    { key: "title", label: "Title (Alphabetical)", getValue: (m) => m.title },
    { key: "location", label: "Location", getValue: (m) => formatLocation(m.location) },
  ];

  const contactSortOptions: ISortOption<ContactModel>[] = [
    { key: "displayName", label: "Name (A-Z)", getValue: (c) => c.displayName },
    { key: "email", label: "Email Address", getValue: (c) => c.email || "" },
    { key: "role", label: "User Role", getValue: (c) => c.userRole || "" },
  ];

  const groupSortOptions: ISortOption<GroupModel>[] = [
    { key: "name", label: "Group Name", getValue: (g) => g.name },
    { key: "memberCount", label: "Member Count", getValue: (g) => g.contactIds?.length || 0 },
  ];

  const mediaSortOptions: ISortOption<MediaModel>[] = [
    { key: "title", label: "Title", getValue: (m) => m.title },
    { key: "mediaDate", label: "Media Date", getValue: (m) => m.mediaDate?.formatted || m.createdAt },
    { key: "favouriteRating", label: "Favourite Rating", getValue: (m) => m.favouriteRating ?? 3 },
    { key: "contentType", label: "Media Type", getValue: (m) => m.contentType },
    { key: "createdAt", label: "Date Uploaded", getValue: (m) => m.createdAt },
  ];

  const shareSortOptions: ISortOption<ShareModel>[] = [
    { key: "targetDisplayName", label: "Recipient Name", getValue: (s) => s.targetDisplayName || "" },
    { key: "role", label: "Permission Role", getValue: (s) => s.role },
  ];

  const tagSortOptions: ISortOption<TagModel>[] = [
    { key: "value", label: "Tag Name (A-Z)", getValue: (t) => t.value || "" },
    { key: "tagType", label: "Tag Type", getValue: (t) => t.tagType || "" },
    { key: "owner", label: "User", getValue: (t) => t.ownerDisplayName || "" },
    { key: "createdAt", label: "Created Date", getValue: (t) => t.createdAt || 0 },
  ];

  // Group Options per Entity
  const memoryGroupOptions: IGroupOption<MemoryModel>[] = [
    { key: "favouriteRating", label: "Favourite Rating", getGroupValue: (m) => formatFavouriteRatingGroup(m.favouriteRating) },
    { key: "location", label: "Location", getGroupValue: (m) => formatLocation(m.location) || "Unspecified" },
    { key: "role", label: "Role", getGroupValue: (m) => (m.userRole === "owner" ? "My Memories" : "Shared") },
  ];

  const contactGroupOptions: IGroupOption<ContactModel>[] = [
    { key: "role", label: "Role", getGroupValue: (c) => (c.userRole === "owner" ? "My Contacts" : "Shared Contacts") },
  ];

  const groupGroupOptions: IGroupOption<GroupModel>[] = [
    { key: "role", label: "Role", getGroupValue: (g) => (g.userRole === "owner" ? "My Groups" : "Shared Groups") },
  ];

  const mediaGroupOptions: IGroupOption<MediaModel>[] = [
    MEDIA_FAVOURITE_GROUP_OPTION,
    { key: "type", label: "Media Type", getGroupValue: (m) => (m.contentType.startsWith("video/") ? "Videos" : "Images") },
    { key: "role", label: "Role", getGroupValue: (m) => (m.userRole === "owner" ? "My Media" : "Shared Media") },
  ];

  const tagGroupOptions: IGroupOption<TagModel>[] = [
    { key: "owner", label: "User", getGroupValue: (t) => t.ownerDisplayName || "Unknown User" },
    { key: "tagType", label: "Tag Type", getGroupValue: (t) => (t.tagType ? t.tagType.toUpperCase() : "GENERAL") },
  ];

  const shareGroupOptions: IGroupOption<ShareModel>[] = [
    { key: "role", label: "Role", getGroupValue: (s) => (s.role === "editor" ? "Editor Permissions" : "Viewer Permissions") },
    { key: "sourceType", label: "Item Type", getGroupValue: (s) => s.sourceType.toUpperCase() },
  ];

  const toggleSelectCard = (id: string) => {
    setSelectedCardIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const renderViewSelector = () => (
    <ThemedViewSelector<DesignViewMode>
      activeMode={viewMode}
      onModeChange={handleViewModeChange}
      options={VIEW_MODES}
    />
  );

  return (
    <ThemedScreen
      title="Themed List & Card Workbench"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* Intro Header */}
        <ThemedPanel padding={Spacing.three} elevation="low">
          <View style={styles.headerRow}>
            <View style={[styles.headerIcon, { backgroundColor: badgeBg }]}>
              <Ionicons name="albums-outline" size={24} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="subtitle">Themed List &amp; Card Workbench</ThemedText>
              <ThemedText style={styles.headerDesc}>
                Comprehensive design workbench covering the 5-Tier Item Card architecture (Chip, Compact, Medium, Full, Custom) and the unified ThemedList container with live filtering and sorting.
              </ThemedText>
            </View>
          </View>
        </ThemedPanel>

        {/* Primary Workbench Switcher */}
        <ThemedSegmentedControl
          options={[
            { label: "Card Designs (5 Tiers)", value: "cards", icon: "card-outline" },
            { label: "Themed List & Filtering", value: "lists", icon: "list-outline" },
          ]}
          value={workbenchTab}
          onChange={(val) => handleWorkbenchTabChange(val as "cards" | "lists")}
          size="medium"
        />

        {/* ========================================================================= */}
        {/* TAB 1: CARD DESIGNS (5 TIERS)                                             */}
        {/* ========================================================================= */}
        {workbenchTab === "cards" && (
          <View style={{ gap: Spacing.two }}>
            {/* Overview Card */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
              <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                5-Tier Item Card Consistency Standard
              </ThemedText>
              <ThemedText style={styles.descText}>
                All items in the platform (Contact, Group, Memory, Media, Tag, Share) follow a consistent 5-tier card hierarchy:
              </ThemedText>
              <View style={styles.tierPillRow}>
                <View style={[styles.tierPill, { backgroundColor: badgeBg }]}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>1. Chip</ThemedText>
                  <ThemedText style={styles.tierPillSub}>Inline Related Items</ThemedText>
                </View>
                <View style={[styles.tierPill, { backgroundColor: badgeBg }]}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>2. Compact</ThemedText>
                  <ThemedText style={styles.tierPillSub}>Selectors &amp; Dense Lists</ThemedText>
                </View>
                <View style={[styles.tierPill, { backgroundColor: badgeBg }]}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>3. Medium</ThemedText>
                  <ThemedText style={styles.tierPillSub}>Standard Gallery Cards</ThemedText>
                </View>
                <View style={[styles.tierPill, { backgroundColor: badgeBg }]}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>4. Full</ThemedText>
                  <ThemedText style={styles.tierPillSub}>Partitioned Folio Tray</ThemedText>
                </View>
                <View style={[styles.tierPill, { backgroundColor: badgeBg }]}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 11 }}>5. Custom</ThemedText>
                  <ThemedText style={styles.tierPillSub}>User-Configured Fields</ThemedText>
                </View>
              </View>
            </ThemedView>

            {/* Tier 1 Item Card: ThemedChip Showcase */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  Item Cards: Tier 1 Chip (&lt;ThemedChip&gt;)
                </ThemedText>
                <ComponentRefTag name="REF: <ThemedChip>" />
              </View>
              <ThemedText style={styles.descText}>
                Unified compact inline capsule chip component for displaying related items (Contacts, Groups, Memories, Media, Tags, Shares) across the platform. Aligned with ThemedEditor capsule styling (Emerald for Contacts/Users, Royal Blue for Groups/Messages, Amber for Media, Rose Crimson for Memories, Tangerine for Tags, Heliotrope for Shares). Features native slots for Platform Symbols (Owner profile badge, 5-star gold star) and dismiss actions.
              </ThemedText>

              {/* 1. Entity Chips (ThemedEditor Aligned Capsule Tokens) */}
              <View style={{ marginBottom: Spacing.two }}>
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 8, color: textMuted }}>
                  1. ENTITY CHIPS (THEMEDEDITOR ALIGNED CAPSULE TOKENS)
                </ThemedText>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  <ThemedChip
                    entityType="contact"
                    label="Alice Walker"
                    id="c-demo-1"
                  />
                  <ThemedChip
                    entityType="group"
                    label="Family Heritage"
                    id="g-demo-1"
                  />
                  <ThemedChip
                    entityType="memory"
                    label="Kyoto Bamboo Grove Walk"
                    id="m-demo-1"
                  />
                  <ThemedChip
                    entityType="media"
                    label="Kyoto_Bamboo_Path.jpg"
                    thumbnailUrl="https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=100&auto=format&fit=crop&q=60"
                    id="med-demo-1"
                  />
                  <ThemedChip
                    entityType="tag"
                    tagType="memory"
                    label="Wildflower Ridge"
                    id="tag-demo-1"
                  />
                  <ThemedChip
                    entityType="share"
                    label="Kyoto Bamboo Grove Walk"
                    secondaryLabel="Mara Solis"
                    id="s-demo-1"
                  />
                  <ThemedChip
                    entityType="user"
                    label="Mara Solis"
                    secondaryLabel="mara.solis@example.com"
                    id="u-demo-1"
                  />
                  <ThemedChip
                    entityType="message"
                    label="Kyoto Photos Ready"
                    secondaryLabel="Alice Walker"
                    id="msg-demo-1"
                  />
                </View>
              </View>

              {/* 2. Platform Symbols: Owner Profile Badge & 5-Star Gold Star */}
              <View style={{ marginBottom: Spacing.two }}>
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 8, color: textMuted }}>
                  2. PLATFORM SYMBOLS (OWNER BADGE &amp; 5-STAR GOLD STAR)
                </ThemedText>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  <ThemedChip
                    entityType="contact"
                    label="Alice Walker"
                    isOwner={true}
                    id="c-demo-2"
                  />
                  <ThemedChip
                    entityType="memory"
                    label="Kyoto Bamboo Grove Walk"
                    isFiveStar={true}
                    id="m-demo-2"
                  />
                  <ThemedChip
                    entityType="media"
                    label="Kodachrome Slide 1974"
                    thumbnailUrl="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=100&auto=format&fit=crop&q=60"
                    isOwner={true}
                    isFiveStar={true}
                    id="med-demo-2"
                  />
                </View>
              </View>

              {/* 3. Interactive Dismiss / Remove Actions & Compact Density */}
              <View>
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 8, color: textMuted }}>
                  3. REMOVABLE CHIPS &amp; COMPACT DENSITY
                </ThemedText>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                  <ThemedChip
                    entityType="tag"
                    tagType="contact"
                    label="Family"
                    onRemove={() => {}}
                    compact={false}
                  />
                  <ThemedChip
                    entityType="tag"
                    tagType="general"
                    label="1970s"
                    onRemove={() => {}}
                    compact={true}
                  />
                  <ThemedChip
                    entityType="memory"
                    label="Field Voice Notes"
                    compact={true}
                    isFiveStar={true}
                    onRemove={() => {}}
                  />
                </View>
              </View>
            </ThemedView>

            {/* Tier 2 Item Card: ThemedCompactCard Showcase */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  Item Cards: Tier 2 Compact (&lt;ThemedCompactCard&gt;)
                </ThemedText>
                <ComponentRefTag name="REF: <ThemedCompactCard>" />
              </View>
              <ThemedText style={styles.descText}>
                Compact single-item card designed for Selectors, dense list views, and linked media sections. Standardized across all 6 entities: Contact (Name + Avatar), Group (Name + Icon), Memory (Title + Date + Thumbnail), Media (Square thumbnail with badge), Tag (Name + TagType icon), and Share (Compact shared item snippet + Recipient name).
              </ThemedText>

              <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: Spacing.one }}>
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted }}>
                  COMPACT SPECIMENS (ALL 6 ENTITIES)
                </ThemedText>
                <ThemedButton
                  title={cardSelectionMode ? "Exit Select Mode" : "Test Select Mode"}
                  variant={cardSelectionMode ? "primary" : "secondary"}
                  size="small"
                  onPress={() => setCardSelectionMode(!cardSelectionMode)}
                />
              </View>

              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: Spacing.two, marginTop: Spacing.one }}>
                {/* 1. Contact Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="contact"
                    id={MOCK_CONTACTS[0].id}
                    title={MOCK_CONTACTS[0].displayName}
                    isOwner={true}
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(MOCK_CONTACTS[0].id)}
                    onSelect={() => toggleSelectCard(MOCK_CONTACTS[0].id)}
                  />
                </View>

                {/* 2. Group Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="group"
                    id={MOCK_GROUPS[0].id}
                    title={MOCK_GROUPS[0].name}
                    isOwner={true}
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(MOCK_GROUPS[0].id)}
                    onSelect={() => toggleSelectCard(MOCK_GROUPS[0].id)}
                  />
                </View>

                {/* 3. Memory Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="memory"
                    id={MOCK_MEMORIES[0].id}
                    title={MOCK_MEMORIES[0].title}
                    date={MOCK_MEMORIES[0].date}
                    thumbnailUrl={MOCK_MEMORIES[0].media?.[0]?.downloadUrl}
                    isFiveStar={true}
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(MOCK_MEMORIES[0].id)}
                    onSelect={() => toggleSelectCard(MOCK_MEMORIES[0].id)}
                  />
                </View>

                {/* 4. Media Compact */}
                <View style={{ minWidth: 90, flex: 0 }}>
                  <ThemedCompactCard
                    entityType="media"
                    id={MOCK_MEDIA[0].id}
                    title={MOCK_MEDIA[0].title}
                    thumbnailUrl={MOCK_MEDIA[0].url}
                    isOwner={true}
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(MOCK_MEDIA[0].id)}
                    onSelect={() => toggleSelectCard(MOCK_MEDIA[0].id)}
                  />
                </View>

                {/* 5. Tag Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="tag"
                    id={DEMO_TAG.id}
                    title={DEMO_TAG.name}
                    tagType="memory"
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(DEMO_TAG.id)}
                    onSelect={() => toggleSelectCard(DEMO_TAG.id)}
                  />
                </View>

                {/* 6. Share Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="share"
                    id={DEMO_SHARE.id}
                    sharedItem={DEMO_SHARE.sharedItem}
                    recipientName={DEMO_SHARE.recipientDisplayName}
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(DEMO_SHARE.id)}
                    onSelect={() => toggleSelectCard(DEMO_SHARE.id)}
                  />
                </View>

                {/* 7. User Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="user"
                    id="u-demo-1"
                    title="Mara Solis"
                    date="mara.solis@example.com"
                    isOwner={true}
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes("u-demo-1")}
                    onSelect={() => toggleSelectCard("u-demo-1")}
                  />
                </View>

                {/* 8. Message Compact */}
                <View style={{ minWidth: 260, flex: 1 }}>
                  <ThemedCompactCard
                    entityType="message"
                    id={DEMO_MESSAGE.id}
                    title={DEMO_MESSAGE.subject}
                    date="16-Aug-2026"
                    selectionMode={cardSelectionMode}
                    isSelected={selectedCardIds.includes(DEMO_MESSAGE.id)}
                    onSelect={() => toggleSelectCard(DEMO_MESSAGE.id)}
                  />
                </View>
              </View>
            </ThemedView>

            {/* Tier 3 Item Card: ThemedMediumCard Showcase */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  Item Cards: Tier 3 Medium (&lt;ThemedMediumCard&gt;)
                </ThemedText>
                <ComponentRefTag name="REF: <ThemedMediumCard>" />
              </View>
              <ThemedText style={styles.descText}>
                Standard balanced catalog card for lists, feeds, and galleries. Displays header metadata, leading icon/thumbnail, short narrative preview, and linked items. Specifically adheres to the single-section Contact &amp; Groups standard: Contacts and Groups are rendered as Chip cards together in a single unified tray.
              </ThemedText>

              {/* Entity Selector for Tier 3 Medium Cards */}
              <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: Spacing.two, marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                <ThemedText type="defaultSemiBold" style={{ fontSize: 12 }}>
                  Preview Entity Medium Card:
                </ThemedText>
                <ThemedSegmentedControl
                  options={[
                    { value: "memory", label: "Memory" },
                    { value: "media", label: "Media" },
                    { value: "contact", label: "Contact" },
                    { value: "group", label: "Group" },
                    { value: "tag", label: "Tag" },
                    { value: "share", label: "Share" },
                    { value: "user", label: "User" },
                    { value: "message", label: "Message" },
                  ]}
                  value={selectedMediumEntity}
                  onChange={(val) => handleMediumEntityChange(val as CardEntityType)}
                  style={{ minWidth: 320 }}
                />
              </View>

              <View style={{ marginTop: 4 }}>
                {selectedMediumEntity === "memory" && (
                  <ThemedMediumCard
                    entityType="memory"
                    id={MOCK_MEMORIES[0].id}
                    title={MOCK_MEMORIES[0].title}
                    date={MOCK_MEMORIES[0].date}
                    formattedDate="August 15, 2026"
                    description={MOCK_MEMORIES[0].description}
                    ownerDisplayName={MOCK_MEMORIES[0].ownerDisplayName}
                    thumbnailUrl={MOCK_MEMORIES[0].media?.[0]?.downloadUrl}
                    isOwner={true}
                    isFiveStar={true}
                    mediaList={[
                      { id: "med-1", title: "Kyoto Bamboo Path", thumbnailUrl: MOCK_MEDIA[0].url },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName, isOwner: true },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    tagsList={[
                      { id: "tag-1", name: "Kyoto", tagType: "memory" },
                    ]}
                    sharesList={[
                      { id: "share-1", recipientDisplayName: "Alice Walker" },
                    ]}
                  />
                )}

                {selectedMediumEntity === "media" && (
                  <ThemedMediumCard
                    entityType="media"
                    id={MOCK_MEDIA[0].id}
                    title={MOCK_MEDIA[0].title}
                    date={MOCK_MEDIA[0].mediaDate?.formatted || "15-Aug-2026"}
                    formattedDate="August 15, 2026"
                    description="High-resolution Kodachrome archival scan from family expedition."
                    ownerDisplayName="Mara Solis"
                    thumbnailUrl={MOCK_MEDIA[0].url}
                    isOwner={true}
                    isFiveStar={true}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    memoriesList={[
                      { id: "mem-1", title: "Kyoto Bamboo Grove Walk", favouriteRating: 5 },
                    ]}
                    tagsList={[
                      { id: "tag-2", name: "Bamboo Path", tagType: "media" },
                    ]}
                    sharesList={[
                      { id: "share-2", recipientDisplayName: "Charlie Brown" },
                    ]}
                  />
                )}

                {selectedMediumEntity === "contact" && (
                  <ThemedMediumCard
                    entityType="contact"
                    id={MOCK_CONTACTS[0].id}
                    title={MOCK_CONTACTS[0].displayName}
                    date="Added Jan 10, 2026"
                    formattedDate="January 10, 2026"
                    description="Archival researcher and primary collaborator for Kyoto expedition."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    memoriesList={[
                      { id: "mem-1", title: "Kyoto Bamboo Grove Walk", favouriteRating: 5 },
                    ]}
                    tagsList={[
                      { id: "tag-3", name: "Colleague", tagType: "contact" },
                    ]}
                    sharesList={[
                      { id: "share-1", recipientDisplayName: "Charlie Brown" },
                    ]}
                  />
                )}

                {selectedMediumEntity === "group" && (
                  <ThemedMediumCard
                    entityType="group"
                    id={MOCK_GROUPS[0].id}
                    title={MOCK_GROUPS[0].name}
                    date="Created Jan 1, 2026"
                    formattedDate="January 1, 2026"
                    description={MOCK_GROUPS[0].description}
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName, isOwner: true },
                      { id: MOCK_CONTACTS[1].id, displayName: MOCK_CONTACTS[1].displayName },
                    ]}
                    memoriesList={[
                      { id: "mem-1", title: "Kyoto Bamboo Grove Walk", favouriteRating: 5 },
                    ]}
                    tagsList={[
                      { id: "tag-4", name: "Heritage", tagType: "group" },
                    ]}
                    sharesList={[
                      { id: "share-3", recipientDisplayName: "Arthur Pendelton" },
                    ]}
                  />
                )}

                {selectedMediumEntity === "tag" && (
                  <ThemedMediumCard
                    entityType="tag"
                    id={DEMO_TAG.id}
                    title={DEMO_TAG.name}
                    date="Tag Category: memory"
                    formattedDate="Memory Tag"
                    tagType="memory"
                    description="Expedition sites, scenic viewpoints, and flora sightings across the Kansai region."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    mediaList={[
                      { id: "med-1", title: "Kyoto Bamboo Path", thumbnailUrl: MOCK_MEDIA[0].url },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                  />
                )}

                {selectedMediumEntity === "share" && (
                  <ThemedMediumCard
                    entityType="share"
                    id={DEMO_SHARE.id}
                    title={`Shared with ${DEMO_SHARE.recipientDisplayName}`}
                    date="Shared on Aug 15, 2026"
                    formattedDate="August 15, 2026"
                    description="Viewer access granted for the Kyoto Bamboo Grove Walk memory folio."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    mediaList={[
                      { id: "med-1", title: "Kyoto Bamboo Path", thumbnailUrl: MOCK_MEDIA[0].url },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: DEMO_SHARE.recipientDisplayName },
                    ]}
                  />
                )}

                {selectedMediumEntity === "user" && (
                  <UserCard
                    user={{
                      uid: "u-demo-1",
                      userId: "u-demo-1",
                      displayName: "Mara Solis",
                      email: "mara.solis@example.com",
                      userType: "user",
                      active: true,
                      authProvider: "google",
                      createdAt: Date.parse("2026-01-10T10:00:00.000Z"),
                      lastSignInTime: Date.parse("2026-10-21T06:00:00.000Z"),
                      allContactsGroupId: "grp-all-contacts-1",
                      allContactsGroup: {
                        id: "grp-all-contacts-1",
                        name: "All Contacts (Mara)",
                        contactIds: ["cnt-1", "cnt-2"],
                      } as any,
                      claims: { admin: true, tester: true },
                    }}
                    viewMode="medium"
                  />
                )}

                {selectedMediumEntity === "message" && (
                  <MessageCard
                    message={DEMO_MESSAGE as any}
                    viewMode="medium"
                    onPress={() => {}}
                  />
                )}
              </View>
            </ThemedView>

            {/* Tier 4 Item Card: ThemedFullCard Showcase */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  Item Cards: Tier 4 Full (&lt;ThemedFullCard&gt;)
                </ThemedText>
                <ComponentRefTag name="REF: <ThemedFullCard>" />
              </View>
              <ThemedText style={styles.descText}>
                Option A: Partitioned Ledger Folio. Single item view displaying comprehensive information across segregated tactile trays: Provenance header, body narrative, Media Highlights (Compact cards), Contact &amp; Groups (single section with Chips), Categorization (Tags as Chips), and Sharing &amp; Access (Shares as Chips).
              </ThemedText>

              {/* Entity Selector for Tier 4 Full Cards */}
              <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: Spacing.two, marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                <ThemedText type="defaultSemiBold" style={{ fontSize: 12 }}>
                  Preview Entity Full Card (Option A Folio):
                </ThemedText>
                <ThemedSegmentedControl
                  options={[
                    { value: "memory", label: "Memory" },
                    { value: "media", label: "Media" },
                    { value: "contact", label: "Contact" },
                    { value: "group", label: "Group" },
                    { value: "tag", label: "Tag" },
                    { value: "share", label: "Share" },
                    { value: "user", label: "User" },
                    { value: "message", label: "Message" },
                  ]}
                  value={selectedFullEntity}
                  onChange={(val) => handleFullEntityChange(val as CardEntityType)}
                  style={{ minWidth: 320 }}
                />
              </View>

              <View style={{ marginTop: 4 }}>
                {selectedFullEntity === "memory" && (
                  <ThemedFullCard
                    entityType="memory"
                    id={MOCK_MEMORIES[0].id}
                    title={MOCK_MEMORIES[0].title}
                    date={MOCK_MEMORIES[0].date}
                    formattedDate="August 15, 2026"
                    description={MOCK_MEMORIES[0].description}
                    ownerDisplayName={MOCK_MEMORIES[0].ownerDisplayName}
                    isOwner={true}
                    isFiveStar={true}
                    userRole="owner"
                    mediaList={[
                      { id: "med-1", title: "Kyoto Bamboo Path", thumbnailUrl: MOCK_MEDIA[0].url },
                      { id: "med-2", title: "Matterhorn Summit", thumbnailUrl: MOCK_MEDIA[1].url },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName, isOwner: true },
                      { id: MOCK_CONTACTS[1].id, displayName: MOCK_CONTACTS[1].displayName },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    tagsList={[
                      { id: "tag-1", name: "Kyoto Travel", tagType: "memory" },
                      { id: "tag-2", name: "Arashiyama", tagType: "location" },
                      { id: "tag-3", name: "Summer 2026", tagType: "general" },
                    ]}
                    sharesList={[
                      { id: "share-1", recipientDisplayName: "Arthur Pendelton" },
                      { id: "share-2", recipientDisplayName: "Alice Walker" },
                    ]}
                  />
                )}

                {selectedFullEntity === "media" && (
                  <ThemedFullCard
                    entityType="media"
                    id={MOCK_MEDIA[0].id}
                    title={MOCK_MEDIA[0].title}
                    date={MOCK_MEDIA[0].mediaDate?.formatted || "15-Aug-2026"}
                    formattedDate="August 15, 2026"
                    description="High-resolution photographic capture showing sunlight penetrating the central bamboo canopies of Arashiyama."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    thumbnailUrl={MOCK_MEDIA[0].url}
                    memoriesList={[
                      { id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName, isOwner: true },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    tagsList={[
                      { id: "tag-2", name: "Arashiyama", tagType: "location" },
                      { id: "tag-3", name: "Photography", tagType: "media" },
                    ]}
                    sharesList={[
                      { id: "share-2", recipientDisplayName: "Charlie Brown" },
                    ]}
                  />
                )}

                {selectedFullEntity === "contact" && (
                  <ThemedFullCard
                    entityType="contact"
                    id={MOCK_CONTACTS[0].id}
                    title={MOCK_CONTACTS[0].displayName}
                    date="Added Jan 10, 2026"
                    formattedDate="January 10, 2026"
                    description="Principal archival curator and botanical expedition collaborator based in Kyoto."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    userRole="owner"
                    memoriesList={[
                      { id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    tagsList={[
                      { id: "tag-4", name: "Curator", tagType: "contact" },
                      { id: "tag-5", name: "Japan Expedition", tagType: "general" },
                    ]}
                    sharesList={[
                      { id: "share-1", recipientDisplayName: "Arthur Pendelton" },
                    ]}
                  />
                )}

                {selectedFullEntity === "group" && (
                  <ThemedFullCard
                    entityType="group"
                    id={MOCK_GROUPS[0].id}
                    title={MOCK_GROUPS[0].name}
                    date="Established Jan 1, 2026"
                    formattedDate="January 1, 2026"
                    description={MOCK_GROUPS[0].description}
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    userRole="owner"
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName, isOwner: true },
                      { id: MOCK_CONTACTS[1].id, displayName: MOCK_CONTACTS[1].displayName },
                      { id: MOCK_CONTACTS[2].id, displayName: MOCK_CONTACTS[2].displayName },
                    ]}
                    memoriesList={[
                      { id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title },
                      { id: MOCK_MEMORIES[1].id, title: MOCK_MEMORIES[1].title },
                    ]}
                    tagsList={[
                      { id: "tag-6", name: "Family Lineage", tagType: "group" },
                    ]}
                    sharesList={[
                      { id: "share-3", recipientDisplayName: "Arthur Pendelton" },
                    ]}
                  />
                )}

                {selectedFullEntity === "tag" && (
                  <ThemedFullCard
                    entityType="tag"
                    id={DEMO_TAG.id}
                    title={DEMO_TAG.name}
                    date="Tag Category: memory"
                    formattedDate="Memory Tag"
                    description="Universal archival classification for flora, trails, and scenic landmarks across Kansai expeditions."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    userRole="owner"
                    mediaList={[
                      { id: "med-1", title: "Kyoto Bamboo Path", thumbnailUrl: MOCK_MEDIA[0].url },
                    ]}
                    memoriesList={[
                      { id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName },
                    ]}
                    groupsList={[
                      { id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name },
                    ]}
                    tagsList={[
                      { id: "tag-7", name: "Botanical", tagType: "tag" },
                    ]}
                  />
                )}

                {selectedFullEntity === "share" && (
                  <ThemedFullCard
                    entityType="share"
                    id={DEMO_SHARE.id}
                    title={`Shared Resource: ${DEMO_SHARE.sharedItem.title}`}
                    date="Shared on Aug 15, 2026"
                    formattedDate="August 15, 2026"
                    description="Viewer collaborator permission granted to Arthur Pendelton for the Kyoto Bamboo Grove Walk archival memory folio."
                    ownerDisplayName="Mara Solis"
                    isOwner={true}
                    userRole="owner"
                    mediaList={[
                      { id: "med-1", title: "Kyoto Bamboo Path", thumbnailUrl: MOCK_MEDIA[0].url },
                    ]}
                    memoriesList={[
                      { id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title },
                    ]}
                    contactsList={[
                      { id: MOCK_CONTACTS[0].id, displayName: DEMO_SHARE.recipientDisplayName },
                    ]}
                    sharesList={[
                      { id: "share-1", recipientDisplayName: DEMO_SHARE.recipientDisplayName },
                    ]}
                  />
                )}

                {selectedFullEntity === "user" && (
                  <UserCard
                    user={{
                      uid: "u-demo-1",
                      userId: "u-demo-1",
                      displayName: "Mara Solis",
                      email: "mara.solis@example.com",
                      userType: "user",
                      active: true,
                      authProvider: "google",
                      createdAt: Date.parse("2026-01-10T10:00:00.000Z"),
                      lastSignInTime: Date.parse("2026-10-21T06:00:00.000Z"),
                      allContactsGroupId: "grp-all-contacts-1",
                      allContactsGroup: {
                        id: "grp-all-contacts-1",
                        name: "All Contacts (Mara)",
                        contactIds: ["cnt-1", "cnt-2"],
                      } as any,
                      claims: { admin: true, tester: true },
                    }}
                    viewMode="full"
                  />
                )}

                {selectedFullEntity === "message" && (
                  <MessageCard
                    message={DEMO_MESSAGE as any}
                    viewMode="full"
                    onPress={() => {}}
                  />
                )}
              </View>
            </ThemedView>

            {/* Tier 5 Item Card: ThemedCustomCard Showcase */}
            <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  Item Cards: Tier 5 Custom (&lt;ThemedCustomCard&gt;)
                </ThemedText>
                <ComponentRefTag name="REF: <ThemedCustomCard>" />
              </View>
              <ThemedText style={styles.descText}>
                User-configurable item card with dedicated Configuration Per Entity (Memory, Media, Contact, Group, Tag, Share). Each entity has independent field and section configurations driven by Config.CustomCard per user. When unconfigured, automatically defaults to the Compact definition.
              </ThemedText>

              {/* Interactive Configuration Controls Per Entity */}
              <View style={[styles.configPanel, { backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderColor }]}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 12 }}>
                    Configure Entity Card:
                  </ThemedText>
                  <ThemedSegmentedControl
                    options={[
                      { value: "memory", label: "Memory" },
                      { value: "media", label: "Media" },
                      { value: "contact", label: "Contact" },
                      { value: "group", label: "Group" },
                      { value: "tag", label: "Tag" },
                      { value: "share", label: "Share" },
                      { value: "message", label: "Message" },
                    ]}
                    value={selectedConfigEntity}
                    onChange={(val) => handleConfigEntityChange(val as CustomConfigEntity)}
                    style={{ minWidth: 320 }}
                  />
                </View>

                {/* Contextual Field Toggles for selected entity */}
                <ThemedText type="stamp" style={{ fontSize: 9, letterSpacing: 1, color: textMuted, marginBottom: 6 }}>
                  FIELDS ({selectedConfigEntity.toUpperCase()})
                </ThemedText>
                <View style={[styles.toggleRow, { marginBottom: 10 }]}>
                  {ENTITY_CONFIG_OPTIONS[selectedConfigEntity].fields.map((f) => {
                    const isChecked = userCustomCardConfig[selectedConfigEntity]?.fields?.includes(f.key) ?? false;
                    return (
                      <ThemedToggle
                        key={`toggle-field-${selectedConfigEntity}-${f.key}`}
                        label={f.label}
                        value={isChecked}
                        onValueChange={(val) =>
                          setUserCustomCardConfig((prev) => {
                            const cur = prev[selectedConfigEntity]?.fields || [];
                            const updated = val ? Array.from(new Set([...cur, f.key])) : cur.filter((k) => k !== f.key);
                            return {
                              ...prev,
                              [selectedConfigEntity]: {
                                ...prev[selectedConfigEntity],
                                fields: updated,
                              },
                            };
                          })
                        }
                      />
                    );
                  })}
                </View>

                {/* Contextual Section Toggles for selected entity */}
                <ThemedText type="stamp" style={{ fontSize: 9, letterSpacing: 1, color: textMuted, marginBottom: 6 }}>
                  LINKED SECTIONS ({selectedConfigEntity.toUpperCase()})
                </ThemedText>
                <View style={styles.toggleRow}>
                  {ENTITY_CONFIG_OPTIONS[selectedConfigEntity].sections.map((s) => {
                    const isChecked = userCustomCardConfig[selectedConfigEntity]?.sections?.includes(s.key) ?? false;
                    return (
                      <ThemedToggle
                        key={`toggle-section-${selectedConfigEntity}-${s.key}`}
                        label={s.label}
                        value={isChecked}
                        onValueChange={(val) =>
                          setUserCustomCardConfig((prev) => {
                            const cur = prev[selectedConfigEntity]?.sections || [];
                            const updated = val ? Array.from(new Set([...cur, s.key])) : cur.filter((k) => k !== s.key);
                            return {
                              ...prev,
                              [selectedConfigEntity]: {
                                ...prev[selectedConfigEntity],
                                sections: updated,
                              },
                            };
                          })
                        }
                      />
                    );
                  })}
                </View>
              </View>

              {/* Specimens for all 6 entities showing their individual custom configs */}
              <View style={{ gap: Spacing.two, marginTop: Spacing.one }}>
                {/* 1. Custom Memory Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted }}>
                  SPECIMEN: CUSTOM MEMORY CARD (CONFIGURED FOR MEMORIES)
                </ThemedText>
                <ThemedCustomCard
                  entityType="memory"
                  id={MOCK_MEMORIES[0].id}
                  item={{
                    ...MOCK_MEMORIES[0],
                    mediaList: [{ id: "med-1", title: "Bamboo Grove", thumbnailUrl: MOCK_MEDIA[0].url }],
                    contactsList: [{ id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName, isOwner: true }],
                    groupsList: [{ id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name }],
                    tagsList: [{ id: "tag-1", name: "Kyoto", tagType: "memory" }],
                    sharesList: [{ id: "shr-1", targetTitle: "Kyoto Bamboo Grove Walk", recipientName: "Alice Walker" }],
                  }}
                  config={userCustomCardConfig.memory}
                  isOwner={true}
                  isFiveStar={true}
                />

                {/* 2. Custom Media Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM MEDIA CARD (CONFIGURED FOR MEDIA)
                </ThemedText>
                <ThemedCustomCard
                  entityType="media"
                  id={MOCK_MEDIA[0].id}
                  item={{
                    ...MOCK_MEDIA[0],
                    memoriesList: [{ id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title }],
                    contactsList: [{ id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName }],
                    groupsList: [{ id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name }],
                    tagsList: [{ id: "tag-2", name: "Nature", tagType: "media" }],
                    sharesList: [{ id: "shr-2", targetTitle: "Kyoto_Bamboo_Path.jpg", recipientName: "Charlie Brown" }],
                  }}
                  config={userCustomCardConfig.media}
                  isOwner={true}
                  isFiveStar={true}
                />

                {/* 3. Custom Contact Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM CONTACT CARD (CONFIGURED FOR CONTACTS)
                </ThemedText>
                <ThemedCustomCard
                  entityType="contact"
                  id={MOCK_CONTACTS[0].id}
                  item={{
                    ...MOCK_CONTACTS[0],
                    groupsList: [{ id: MOCK_GROUPS[0].id, name: MOCK_GROUPS[0].name }],
                    memoriesList: [{ id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title }],
                    tagsList: [{ id: "tag-3", name: "Family", tagType: "contact" }],
                  }}
                  config={userCustomCardConfig.contact}
                  isOwner={true}
                />

                {/* 4. Custom Group Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM GROUP CARD (CONFIGURED FOR GROUPS)
                </ThemedText>
                <ThemedCustomCard
                  entityType="group"
                  id={MOCK_GROUPS[0].id}
                  item={{
                    ...MOCK_GROUPS[0],
                    contactsList: [{ id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName }],
                    memoriesList: [{ id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title }],
                    sharesList: [{ id: "shr-3", targetTitle: "Family Heritage", recipientName: "Alice Walker" }],
                  }}
                  config={userCustomCardConfig.group}
                  isOwner={true}
                />

                {/* 5. Custom Tag Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM TAG CARD (CONFIGURED FOR TAGS)
                </ThemedText>
                <ThemedCustomCard
                  entityType="tag"
                  id={DEMO_TAG.id}
                  item={{
                    ...DEMO_TAG,
                    description: "Expedition sites, scenic viewpoints, and flora sightings across the Kansai region.",
                    memoriesList: [{ id: MOCK_MEMORIES[0].id, title: MOCK_MEMORIES[0].title }],
                    mediaList: [{ id: "med-1", title: "Bamboo Grove", thumbnailUrl: MOCK_MEDIA[0].url }],
                    contactsList: [{ id: MOCK_CONTACTS[0].id, displayName: MOCK_CONTACTS[0].displayName }],
                  }}
                  config={userCustomCardConfig.tag}
                  isOwner={true}
                />

                {/* 6. Custom Share Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM SHARE CARD (CONFIGURED FOR SHARES)
                </ThemedText>
                <ThemedCustomCard
                  entityType="share"
                  id={DEMO_SHARE.id}
                  item={{
                    ...DEMO_SHARE,
                    role: "viewer",
                    date: "15-Aug-2026",
                    sharesList: [{ id: "shr-1", targetTitle: "Kyoto Bamboo Grove Walk", recipientName: "Arthur Pendelton" }],
                  }}
                  config={userCustomCardConfig.share}
                  isOwner={true}
                />

                {/* 7. Custom User Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM USER CARD (CONFIGURED FOR USERS)
                </ThemedText>
                <ThemedCustomCard
                  entityType="user"
                  id="u-demo-1"
                  item={{
                    id: "u-demo-1",
                    displayName: "Mara Solis",
                    title: "Mara Solis",
                    email: "mara.solis@example.com",
                    userType: "user",
                    authProvider: "google",
                    storageQuota: 10737418240,
                    active: true,
                  }}
                  title="Mara Solis"
                  email="mara.solis@example.com"
                  isOwner={true}
                />

                {/* 8. Custom Message Card */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: CUSTOM MESSAGE CARD (CONFIGURED FOR MESSAGES)
                </ThemedText>
                <ThemedCustomCard
                  entityType="message"
                  id={DEMO_MESSAGE.id}
                  item={{
                    id: DEMO_MESSAGE.id,
                    subject: DEMO_MESSAGE.subject,
                    body: DEMO_MESSAGE.body,
                    senderDisplayName: DEMO_MESSAGE.senderDisplayName,
                    senderEmail: DEMO_MESSAGE.senderEmail,
                    date: "16-Aug-2026",
                    messageType: DEMO_MESSAGE.type,
                    unread: true,
                    recipients: DEMO_MESSAGE.recipients,
                  }}
                  config={userCustomCardConfig.message}
                  isOwner={true}
                />

                {/* 9. Unconfigured Fallback */}
                <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, color: textMuted, marginTop: Spacing.one }}>
                  SPECIMEN: UNCONFIGURED FALLBACK (DEFAULTS TO COMPACT)
                </ThemedText>
                <ThemedCustomCard
                  entityType="memory"
                  id={MOCK_MEMORIES[0].id}
                  item={MOCK_MEMORIES[0]}
                  isOwner={true}
                  isFiveStar={true}
                />
              </View>
            </ThemedView>
          </View>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: LIST & FILTERING WORKBENCH                                         */}
        {/* ========================================================================= */}
        {workbenchTab === "lists" && (
          <View style={{ gap: Spacing.two }}>
            {/* Sandbox Controls Panel */}
            <ThemedPanel padding={Spacing.two} elevation="low">
              <ThemedText type="defaultSemiBold" style={{ fontSize: 13, marginBottom: 8 }}>
                Interactive Sandbox Controls:
              </ThemedText>

              {/* Dataset Entity Selector */}
              <View style={styles.tabBar}>
                {(["memories", "contacts", "groups", "media", "tags", "shares"] as EntityType[]).map((tab) => {
                  const isActive = activeTab === tab;
                  return (
                    <TouchableOpacity
                      key={tab}
                      style={[
                        styles.tabBtn,
                        { backgroundColor: isActive ? tintColor : badgeBg },
                      ]}
                      onPress={() => handleActiveTabChange(tab)}
                      activeOpacity={0.7}
                    >
                      <ThemedText
                        style={[
                          styles.tabBtnText,
                          { color: isActive ? "#FFFFFF" : textMuted, fontWeight: isActive ? "700" : "500" },
                        ]}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {/* View Mode Selector in Controls */}
              <View style={styles.viewModeSection}>
                <ThemedText type="defaultSemiBold" style={styles.sectionLabel}>
                  Card Tier View Mode (Chip, Compact, Medium, Full, Custom, Details):
                </ThemedText>
                {renderViewSelector()}
              </View>

              {/* Toggle Switches */}
              <View style={styles.toggleRow}>
                <ThemedToggle
                  label="Loading State"
                  value={isLoading}
                  onValueChange={setIsLoading}
                />
                <ThemedToggle
                  label="Empty Dataset"
                  value={isEmpty}
                  onValueChange={setIsEmpty}
                />
                <ThemedToggle
                  label="Hover Indicator"
                  value={enableHover}
                  onValueChange={setEnableHover}
                />
              </View>
            </ThemedPanel>

            {/* The Live ThemedList Component Under Test */}
            <ThemedPanel padding={0} elevation="low" style={{ height: 650, minHeight: 600 }}>
              {activeTab === "memories" && (
                <ThemedList<MemoryModel>
                  data={isEmpty ? [] : MOCK_MEMORIES}
                  entityType="memory"
                  sortOptions={memorySortOptions}
                  groupOptions={memoryGroupOptions}
                  persistenceKey="design-list-memories"
                  filterKeys={["title", "description", "location"]}
                  filterPlaceholder="Search memories..."
                  loading={isLoading}
                  emptyText="No memories found."
                  enableHoverEffect={enableHover}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                  renderItem={(memory, _index, _isHovered, itemVm) => (
                    <MemoryCard
                      memory={memory}
                      viewMode={itemVm}
                      config={userCustomCardConfig.memory}
                      showOwner={memory.userRole !== "owner"}
                      onPress={() => {}}
                      onEditPress={() => {}}
                      onShare={() => {}}
                      onDelete={() => {}}
                      availableContacts={MOCK_CONTACTS}
                      availableGroups={MOCK_GROUPS}
                      availableMedia={MOCK_MEDIA}
                      onShareTarget={(targetId, targetType, role) => {
                        console.log("Design sandbox share target:", targetId, targetType, role);
                      }}
                      onUnshare={(shareId) => {
                        console.log("Design sandbox unshare:", shareId);
                      }}
                    />
                  )}
                />
              )}

              {activeTab === "contacts" && (
                <ThemedList<ContactModel>
                  data={isEmpty ? [] : MOCK_CONTACTS}
                  entityType="contact"
                  sortOptions={contactSortOptions}
                  groupOptions={contactGroupOptions}
                  persistenceKey="design-list-contacts"
                  filterKeys={["displayName", "email"]}
                  filterPlaceholder="Search contacts..."
                  loading={isLoading}
                  emptyText="No contacts found."
                  enableHoverEffect={enableHover}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                  renderItem={(contact, _index, _isHovered, itemVm) => (
                    <ContactCard
                      contact={contact}
                      viewMode={itemVm}
                      config={userCustomCardConfig.contact}
                      availableContacts={MOCK_CONTACTS}
                      availableGroups={MOCK_GROUPS}
                      availableMedia={MOCK_MEDIA}
                      onPress={() => {}}
                      onEditPress={() => {}}
                      onShare={() => {}}
                      onDelete={() => {}}
                    />
                  )}
                />
              )}

              {activeTab === "groups" && (
                <ThemedList<GroupModel>
                  data={isEmpty ? [] : MOCK_GROUPS}
                  entityType="group"
                  sortOptions={groupSortOptions}
                  groupOptions={groupGroupOptions}
                  persistenceKey="design-list-groups"
                  filterKeys={["name", "description"]}
                  filterPlaceholder="Search groups..."
                  loading={isLoading}
                  emptyText="No groups found."
                  enableHoverEffect={enableHover}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                  renderItem={(group, _index, _isHovered, itemVm) => (
                    <GroupCard
                      group={group}
                      viewMode={itemVm}
                      config={userCustomCardConfig.group}
                      availableContacts={MOCK_CONTACTS}
                      availableGroups={MOCK_GROUPS}
                      availableMedia={MOCK_MEDIA}
                      onPress={() => {}}
                      onEditPress={() => {}}
                      onShare={() => {}}
                      onDelete={() => {}}
                    />
                  )}
                />
              )}

              {activeTab === "media" && (
                <ThemedList<MediaModel>
                  data={isEmpty ? [] : MOCK_MEDIA}
                  entityType="media"
                  sortOptions={mediaSortOptions}
                  groupOptions={mediaGroupOptions}
                  persistenceKey="design-list-media"
                  filterKeys={["title"]}
                  filterPlaceholder="Search media..."
                  loading={isLoading}
                  emptyText="No media found."
                  enableHoverEffect={enableHover}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                  renderItem={(media, _index, _isHovered, itemVm) => (
                    <MediaCard
                      media={media}
                      viewMode={itemVm}
                      config={userCustomCardConfig.media}
                      availableContacts={MOCK_CONTACTS}
                      availableGroups={MOCK_GROUPS}
                      showOwnerBadge={media.userRole !== "owner"}
                      onPress={() => {}}
                      onEditPress={() => {}}
                      onSharePress={() => {}}
                      onDeletePress={() => {}}
                    />
                  )}
                />
              )}

              {activeTab === "tags" && (
                <ThemedList<TagModel>
                  data={isEmpty ? [] : MOCK_TAGS}
                  entityType="tag"
                  sortOptions={tagSortOptions}
                  groupOptions={tagGroupOptions}
                  persistenceKey="design-list-tags"
                  filterKeys={["value", "tagType", "ownerDisplayName"]}
                  filterPlaceholder="Search tags..."
                  loading={isLoading}
                  emptyText="No tags found."
                  enableHoverEffect={enableHover}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                  renderItem={(tag, _index, _isHovered, itemVm) => {
                    const formattedDate = tag.createdAt ? formatDisplayDate(tag.createdAt) : undefined;
                    const effectiveMedia = tag.mediaList || [];
                    const effectiveMemories = tag.memoriesList || [];
                    const effectiveContacts = tag.contactsList || [];
                    const effectiveGroups = tag.groupsList || [];

                    if (itemVm === "chip") {
                      return (
                        <ThemedChip
                          entityType="tag"
                          id={tag.id}
                          title={tag.value}
                          tagType={tag.tagType}
                          date={formattedDate}
                          onPress={() => {}}
                        />
                      );
                    }

                    if (itemVm === "compact") {
                      return (
                        <ThemedCompactCard
                          entityType="tag"
                          id={tag.id}
                          title={tag.value}
                          tagType={tag.tagType}
                          date={formattedDate}
                          onPress={() => {}}
                        />
                      );
                    }

                    if (itemVm === "medium") {
                      return (
                        <ThemedMediumCard
                          entityType="tag"
                          id={tag.id}
                          title={tag.value}
                          tagType={tag.tagType}
                          date={formattedDate}
                          ownerDisplayName={tag.ownerDisplayName}
                          mediaList={effectiveMedia}
                          memoriesList={effectiveMemories}
                          contactsList={effectiveContacts}
                          groupsList={effectiveGroups}
                          onPress={() => {}}
                        />
                      );
                    }

                    if (itemVm === "full") {
                      return (
                        <ThemedFullCard
                          entityType="tag"
                          id={tag.id}
                          title={tag.value}
                          tagType={tag.tagType}
                          date={formattedDate}
                          ownerDisplayName={tag.ownerDisplayName}
                          mediaList={effectiveMedia}
                          memoriesList={effectiveMemories}
                          contactsList={effectiveContacts}
                          groupsList={effectiveGroups}
                          onPress={() => {}}
                        />
                      );
                    }

                    if (itemVm === "custom") {
                      return (
                        <ThemedCustomCard
                          entityType="tag"
                          id={tag.id}
                          title={tag.value}
                          formattedDate={formattedDate}
                          mediaList={effectiveMedia}
                          memoriesList={effectiveMemories}
                          contactsList={effectiveContacts}
                          groupsList={effectiveGroups}
                          item={tag}
                          config={userCustomCardConfig.tag}
                          onPress={() => {}}
                        />
                      );
                    }

                    // Default: Details row specimen
                    return (
                      <View
                        style={[
                          styles.detailsRowContainer,
                          { backgroundColor: cardBg, borderColor: borderColor },
                        ]}
                      >
                        <TouchableOpacity
                          style={styles.detailsRowMain}
                          onPress={() => {}}
                          activeOpacity={0.7}
                        >
                          <View style={{ flex: 1, gap: 2 }}>
                            <ThemedText style={styles.detailsRowTitle} numberOfLines={1}>
                              {tag.value}
                            </ThemedText>
                            <ThemedText style={styles.detailsRowSubtitle} numberOfLines={1}>
                              Type: {tag.tagType || "general"} • {tag.ownerDisplayName || "Unknown"}
                            </ThemedText>
                          </View>
                        </TouchableOpacity>
                      </View>
                    );
                  }}
                />
              )}

              {activeTab === "shares" && (
                <ThemedList<ShareModel>
                  data={isEmpty ? [] : MOCK_SHARES}
                  entityType="share"
                  sortOptions={shareSortOptions}
                  groupOptions={shareGroupOptions}
                  persistenceKey="design-list-shares"
                  filterKeys={["targetDisplayName", "role"]}
                  filterPlaceholder="Search shares..."
                  loading={isLoading}
                  emptyText="No shares found."
                  enableHoverEffect={enableHover}
                  viewMode={viewMode}
                  onViewModeChange={handleViewModeChange}
                  renderItem={(share, _index, _isHovered, itemVm) => (
                    <SharingCard
                      share={share}
                      viewMode={itemVm}
                      config={userCustomCardConfig.share}
                      onUnshare={() => {}}
                      onRoleChange={() => {}}
                    />
                  )}
                />
              )}
            </ThemedPanel>
          </View>
        )}
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: Spacing.two,
    paddingBottom: Spacing.five,
    gap: Spacing.two,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.two,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  headerDesc: {
    fontSize: 12,
    opacity: 0.7,
    marginTop: 2,
    lineHeight: 17,
  },
  card: {
    padding: Spacing.two,
    borderWidth: 1,
    borderRadius: 0,
    gap: Spacing.two,
  },
  frameHeaderWithRef: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: Spacing.two,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: "600",
  },
  descText: {
    fontSize: 13,
    lineHeight: 18,
    opacity: 0.85,
  },
  tierPillRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.one,
    marginTop: Spacing.one,
  },
  tierPill: {
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderRadius: 0,
    minWidth: 120,
    flex: 1,
  },
  tierPillSub: {
    fontSize: 10,
    opacity: 0.7,
    marginTop: 2,
  },
  configPanel: {
    padding: Spacing.two,
    borderWidth: 1,
    borderRadius: 0,
    marginTop: Spacing.one,
  },
  tabBar: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.one,
    marginBottom: Spacing.two,
  },
  tabBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 0,
  },
  tabBtnText: {
    fontSize: 12,
  },
  toggleRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.three,
    alignItems: "center",
  },
  viewModeSection: {
    marginBottom: Spacing.two,
  },
  sectionLabel: {
    fontSize: 11,
    opacity: 0.7,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
});
